import React, { useState, useCallback, useMemo } from 'react';
import { Typography, Tag, Form, Input, Button } from 'antd';
import { MergeCellsOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CrudPage from '../../../components/common/CrudPage';
import { userService } from '../../../services/cms/userServiceRequest';
import roleService from '../../../services/roleManagementRequest';
import MergeUserModal from './MergeUserModal';

// API chỉ coi role 9999 là super admin (AuthCMS/constants: ADMIN_ROLES.SUPER_ADMIN),
// nên nút phải theo đúng điều kiện đó — tránh hiện nút rồi bấm vào bị 403.
const SUPER_ADMIN_ROLE = 9999;
const MERGE_USER_API = '/miniapp/merge-user';

const { Text } = Typography;

const ensureCdnUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const baseDomain = 'https://cdn.lichviet.org';
  const path = url.startsWith('/') ? url : `/${url}`;
  return baseDomain + path;
};

const STATUS_OPTIONS = [
  { label: 'Duyệt', value: 1 },
  { label: 'Đợi duyệt', value: 0 },
  { label: 'Khóa', value: 2 },
];

const GENDER_OPTIONS = [
  { label: 'Chọn giới tính', value: '' },
  { label: 'Nam', value: 'Nam' },
  { label: 'Nữ', value: 'Nữ' },
  { label: 'Khác', value: 'Khác' },
];

const columns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 80,
    render: (id) => (
      <Link to={`/cms/quan-ly-user/${id}`} className="text-blue-600 hover:text-blue-800 font-bold">
        {id}
      </Link>
    ),
  },
  {
    title: 'HỌ & TÊN',
    key: 'full_name',
    render: (_, r) => (
      <div>
        <Link to={`/cms/quan-ly-user/${r.id}`} className="text-blue-600 hover:text-blue-800 font-bold block">
          {r.full_name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.username}
        </Link>
        <div className="text-xs text-gray-500 mt-1">SĐT: {r.phone || '-'}</div>
        <div className="text-xs text-gray-500">Email: {r.email || '-'}</div>
      </div>
    ),
  },
  {
    title: 'ẢNH ĐẠI DIỆN',
    dataIndex: 'avatar',
    key: 'avatar',
    width: 100,
    render: (u) => u ? <img src={ensureCdnUrl(u)} alt="" className="w-10 h-10 object-cover rounded-full" /> : '-',
  },
  {
    title: 'SỐ LƯỢNG PROFILE',
    dataIndex: 'profile_count',
    key: 'profile_count',
    width: 120,
    align: 'center',
    render: (c) => <Text strong>{c ?? 0}</Text>,
  },
  {
    title: 'TẠO PROFILE CÒN LẠI',
    dataIndex: 'remain_profile_creation',
    key: 'remain_profile_creation',
    width: 150,
    align: 'center',
    render: (c) => c ?? 0,
  },
  {
    title: 'CẬP NHẬT NGÀY SINH CÒN LẠI',
    dataIndex: 'birthday_change_time_left',
    key: 'birthday_change_time_left',
    width: 180,
    align: 'center',
    render: (c) => c ?? 0,
  },
  {
    title: 'TRẠNG THÁI',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (s) => {
      switch (s) {
        case 1:
          return <Tag color="green">DUYỆT</Tag>;
        case 0:
          return <Tag color="blue">ĐỢI DUYỆT</Tag>;
        case 2:
          return <Tag color="red">KHÓA</Tag>;
        // 3: dấu vết gộp tài khoản của CMS cũ. 9: chưa tìm được chỗ nào set giá trị này.
        // Chỉ hiện ở cột, không đưa vào bộ lọc/form để admin không tự gán.
        case 3:
          return <Tag color="default">ĐÃ GỘP</Tag>;
        case 9:
          return <Tag color="default">KHÔNG XÁC ĐỊNH (9)</Tag>;
        default:
          return <Tag color="default">CHƯA RÕ ({s})</Tag>;
      }
    },
  },
  {
    title: 'NGÀY ĐĂNG KÝ',
    dataIndex: 'create_time',
    key: 'create_time',
    width: 150,
  },
];

const QuanLyUser = () => {
  const queryClient = useQueryClient();
  const adminInfo = useSelector((state) => state.user?.info);
  const [isMergeOpen, setIsMergeOpen] = useState(false);

  const { data: roleData } = useQuery({
    queryKey: ['roles-for-select-users'],
    queryFn: () => roleService.getList({ limit: 100 }),
  });

  // value 0 = chưa gán vai trò. API coi role_id=0 là "không lọc" (userService.js:44),
  // nên cùng một value nhưng nhãn phải khác nhau giữa bộ lọc và form.
  const roleList = useMemo(() => {
    const list = roleData?.data || roleData?.roles || roleData || [];
    if (!Array.isArray(list)) return [];
    return list.map((r) => ({ label: r.name, value: r.id }));
  }, [roleData]);

  // Dùng cho form thêm/sửa — khớp role_name API trả về khi role_id = 0
  const roleOptions = useMemo(
    () => [{ label: 'Chọn vai trò', value: 0 }, ...roleList],
    [roleList]
  );

  // Dùng cho bộ lọc
  const roleFilterOptions = useMemo(
    () => [{ label: 'Tất cả vai trò', value: 0 }, ...roleList],
    [roleList]
  );

  const filterFields = useMemo(() => [
    {
      label: 'Vai trò',
      value: 'role_id',
      options: roleFilterOptions,
    },
    {
      label: 'Trạng thái',
      value: 'status',
      options: STATUS_OPTIONS,
    },
  ], [roleFilterOptions]);

  // CrudPage cho phép formFields là hàm ({ isEditMode, record }) — dùng để `required` của
  // Tài khoản / Mật khẩu / First Name bật đúng lúc tạo mới. Trước đây ba ô này chặn bằng
  // `validator` tự chế: có chặn nhưng antd không vẽ dấu * nên người dùng không biết trước (QA-13).
  const formFields = useCallback(({ isEditMode }) => [
    { name: 'id', type: 'input', colClass: 'hidden' },
    { name: 'avatar', label: 'Ảnh đại diện', type: 'upload-cdn', lg: 24 },
    
    {
      name: 'username',
      label: 'Tài khoản',
      lg: 12,
      render: ({ value, onChange }) => (
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.id !== curr.id || prev.username !== curr.username}>
          {({ getFieldValue }) => {
            const isEdit = !!getFieldValue('id');
            // Allow editing if it's not an edit, or if it's an edit but the current value is empty.
            // But wait, getFieldValue('username') might be empty right after render.
            // Usually we just disable it if isEdit. To be safe, disable it if isEdit and we already have a username.
            // Actually the original code was: disabled={isEdit}
            return (
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={isEdit}
                placeholder="Nhập tài khoản"
              />
            );
          }}
        </Form.Item>
      ),
      rules: [{ required: !isEditMode, message: 'Tài khoản không được bỏ trống!' }],
    },
    {
      name: 'password',
      label: 'Mật khẩu',
      lg: 12,
      render: ({ value, onChange }) => (
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.id !== curr.id}>
          {({ getFieldValue }) => {
            const isEdit = !!getFieldValue('id');
            return (
              <Input.Password
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={isEdit ? "Mật khẩu mới (bỏ trống nếu không đổi)" : "Nhập mật khẩu"}
                visibilityToggle
              />
            );
          }}
        </Form.Item>
      ),
      // Sửa: bỏ trống = không đổi mật khẩu, nên chỉ bắt buộc khi tạo mới.
      // Rule `min` của async-validator tự bỏ qua giá trị rỗng khi required = false.
      rules: [
        { required: !isEditMode, message: 'Mật khẩu không được bỏ trống khi tạo mới!' },
        { min: 6, message: 'Mật khẩu phải từ 6 ký tự trở lên!' },
      ],
    },
    { 
      name: 'first_name', 
      label: 'First Name', 
      type: 'input', 
      rules: [{ required: !isEditMode, message: 'Vui lòng nhập First Name!' }],
      lg: 12 
    },
    { name: 'last_name', label: 'Last Name', type: 'input', lg: 12 },
    { name: 'gender', label: 'Giới tính', type: 'select', options: GENDER_OPTIONS, lg: 12 },
    { name: 'role_id', label: 'Vai trò', type: 'select', options: roleOptions, lg: 12 },
    { name: 'birthday', label: 'Ngày sinh', type: 'date', submitFormat: 'YYYY-MM-DD', lg: 12 },
    { name: 'status', label: 'Trạng thái', type: 'select', options: STATUS_OPTIONS, defaultValue: 1, lg: 12 },
    { name: 'email', label: 'Email', type: 'input', lg: 12, rules: [{ type: 'email', message: 'Email không hợp lệ' }] },
    { name: 'address', label: 'Địa chỉ', type: 'input', lg: 12 },
    { name: 'phone', label: 'Số điện thoại', type: 'input', lg: 12 },
    { name: 'phone_contact', label: 'Số điện thoại liên hệ', type: 'input', lg: 12 },
    { name: 'birthday_change_time_left', label: 'Số lần sửa ngày sinh còn lại', type: 'number', lg: 12, defaultValue: 3 },
    { name: 'about', label: 'Mô tả', type: 'textarea', lg: 24 },
  ], [roleOptions]);

  const canMergeUser = useMemo(() => {
    if (adminInfo?.role === SUPER_ADMIN_ROLE) return true;
    return (adminInfo?.permissions || []).some((p) => p.api === MERGE_USER_API);
  }, [adminInfo]);

  const headerActions = canMergeUser ? (
    <Button icon={<MergeCellsOutlined />} onClick={() => setIsMergeOpen(true)}>
      Nhập tài khoản
    </Button>
  ) : null;

  return (
    <>
      <CrudPage
        title="TÀI KHOẢN NGƯỜI DÙNG"
        subTitle="Quản lý thông tin tài khoản người dùng hệ thống"
        itemsName="Tài khoản"
        queryKey="userList"
        service={userService}
        columns={columns}
        formFields={formFields}
        filterFields={filterFields}
        searchPlaceholder="Tìm kiếm thành viên..."
        addButtonLabel="Tạo mới"
        formConfig={{ width: 850 }}
        renderHeaderActions={headerActions}
      />

      <MergeUserModal
        open={isMergeOpen}
        onClose={() => setIsMergeOpen(false)}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['userList'] })}
      />
    </>
  );
};

export default QuanLyUser;
